import { TrackingProp } from 'react-tracking';
import { useCallback, useEffect } from 'react';
import { getLoadingMarkNames } from '../helpers/performance';
import { isProdEnv } from 'helpers/env';

interface TrackPageLoadTimeProps {
  eventType: string;
  tracking: TrackingProp;
  componentNames: string[];
  cartId: number;
  email?: string;
}

/**
 * useTrackPageLoadTime
 *
 * Given a list of component names whose loading times are tracked with useTrackComponentLoadTime,
 * fires an event of the given type with the duration it took for all the components to load. Duration
 * is measured as the time between when the first component starts to load and when the last component finishes.
 *
 * @param componentNames the list of component names being tracked (each name must be passed to a corresponding useTrackComponentLoadTime call)
 * @param eventType the event type to fire to track this event, e.g. "checkoutPageTotalLoadTime"
 * @param tracking tracking prop
 * @param cartId customer cart id
 * @param email customer email
 */
export const useTrackPageLoadTime = ({
  componentNames,
  eventType,
  tracking,
  cartId,
  email
}: TrackPageLoadTimeProps): void => {
  const checkForLoadedComponents = useCallback(() => {
    const [startTimes, endTimes] = getAllComponentStartAndEndLoadTimes(componentNames);

    const finishedLoading = allDefined(startTimes) && allDefined(endTimes);
    if (!finishedLoading) {
      return;
    }

    const firstStartTime = Math.min(...startTimes);
    const lastEndTime = Math.max(...endTimes);
    const loadDuration = Math.round(lastEndTime - firstStartTime);

    const data = { load_time: loadDuration, cart_id: cartId, email };
    tracking.trackEvent({ type: eventType, data });

    if (!isProdEnv()) {
      console.debug(`${eventType} load time: ${loadDuration}ms`);
    }
    // Clear load marks to prevent event being fired again until another load occurs
    clearLoadMarks(componentNames);
  }, [cartId, componentNames, email, eventType, tracking]);

  useEffect(() => {
    const interval = setInterval(checkForLoadedComponents, 1000);

    return () => clearInterval(interval);
  }, [checkForLoadedComponents]);
};

const getAllComponentStartAndEndLoadTimes = (componentNames: string[]) => {
  const startTimes: number[] = [];
  const endTimes: number[] = [];
  componentNames.forEach(name => {
    const [start, end] = getComponentStartAndEndLoadTimes(name);
    startTimes.push(start);
    endTimes.push(end);
  });
  return [startTimes, endTimes];
};

const getComponentStartAndEndLoadTimes = (componentName: string) => {
  // Start and end load times are set via performance.mark() calls in useTrackComponentLoadTime and retrieved here
  const markNames = getLoadingMarkNames(componentName);
  const lastStartMark = performance.getEntriesByName(markNames.start).pop();
  const lastEndMark = performance.getEntriesByName(markNames.end).pop();

  if (lastEndMark?.startTime < lastStartMark?.startTime) {
    return [];
  }

  return [lastStartMark?.startTime, lastEndMark?.startTime];
};

const allDefined = <T>(list: T[]): boolean => list.every(item => item !== undefined && item !== null);

const clearLoadMarks = (componentNames: string[]) => {
  const markNames = componentNames
    .map(getLoadingMarkNames)
    .flatMap(names => [names.start, names.end]);

  markNames.forEach(name => performance.clearMarks(name));
};